"use client";
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-white/5 py-4 px-4">
        <div className="max-w-screen-xl mx-auto flex items-center justify-between">
          <Link href="/landing-page" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-display font-bold text-base">LookMax AI</span>
          </Link>
          <Link href="/landing-page" className="text-sm text-white/50 hover:text-white transition-colors">
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-4xl font-display font-bold mb-2">Privacy Policy</h1>
        <p className="text-white/40 text-sm mb-12">Last updated: March 20, 2026</p>

        <div className="space-y-10 text-white/70 leading-relaxed">

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
            <p>
              Welcome to LookMax AI ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services at <strong className="text-white">lookmaxers.com</strong>.
            </p>
            <p className="mt-3">
              Please read this policy carefully. If you disagree with its terms, please discontinue use of our services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>
            <p className="mb-3">We collect information you provide directly to us, including:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong className="text-white">Account Information:</strong> Name, email address, and password when you register.</li>
              <li><strong className="text-white">Photos:</strong> Facial images you upload for AI analysis. These are encrypted and stored securely.</li>
              <li><strong className="text-white">Payment Information:</strong> Billing details processed securely through Stripe. We do not store your full card details.</li>
              <li><strong className="text-white">Usage Data:</strong> Information about how you interact with our services, including pages visited and features used.</li>
              <li><strong className="text-white">Device Information:</strong> IP address, browser type, operating system, and device identifiers.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. How We Use Your Information</h2>
            <p className="mb-3">We use the information we collect to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Provide, operate, and improve our AI analysis services</li>
              <li>Process transactions and send related information</li>
              <li>Send you technical notices, updates, and support messages</li>
              <li>Respond to your comments and questions</li>
              <li>Monitor and analyze usage patterns to improve user experience</li>
              <li>Detect, prevent, and address technical issues or fraudulent activity</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Photo & Biometric Data</h2>
            <p className="mb-3">
              We take the handling of your photos and any derived biometric data very seriously:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Photos are encrypted in transit and at rest using industry-standard encryption.</li>
              <li>We never sell, share, or license your photos or biometric data to third parties.</li>
              <li>Photos are used solely to generate your personalized attractiveness analysis and roadmap.</li>
              <li>You may delete your photos and all associated data at any time from your account settings.</li>
              <li>Upon account deletion, all photos and derived data are permanently removed from our systems within 30 days.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Sharing of Information</h2>
            <p className="mb-3">We do not sell your personal information. We may share your information with:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li><strong className="text-white">Service Providers:</strong> Third-party vendors who assist in operating our services (e.g., Stripe for payments, Supabase for data storage, OpenAI for AI analysis). These parties are bound by confidentiality agreements.</li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law, court order, or governmental authority.</li>
              <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, with appropriate notice to you.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time by contacting us at{' '}
              <a href="mailto:lookmaxers.ai@gmail.com" className="text-white underline hover:text-white/80 transition-colors">lookmaxers.ai@gmail.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Cookies & Tracking</h2>
            <p>
              We use cookies and similar tracking technologies to track activity on our service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, some features of our service may not function properly without cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Your Rights</h2>
            <p className="mb-3">Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your personal data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Data portability (receive your data in a structured format)</li>
              <li>Withdraw consent at any time where processing is based on consent</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, contact us at{' '}
              <a href="mailto:lookmaxers.ai@gmail.com" className="text-white underline hover:text-white/80 transition-colors">lookmaxers.ai@gmail.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Children's Privacy</h2>
            <p>
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children under 18. If we become aware that a child under 18 has provided us with personal information, we will take steps to delete such information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="mt-3 p-4 border border-white/10 rounded-xl bg-white/5">
              <p className="text-white font-medium">LookMax AI</p>
              <p className="mt-1">
                Email:{' '}
                <a href="mailto:lookmaxers.ai@gmail.com" className="text-white underline hover:text-white/80 transition-colors">
                  lookmaxers.ai@gmail.com
                </a>
              </p>
              <p className="mt-1">Website: <span className="text-white">lookmaxers.com</span></p>
            </div>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10 px-4 mt-16">
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-display font-bold text-base">LookMax AI</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link href="/privacy-policy" className="hover:text-white transition-colors text-white">Privacy Policy</Link>
            <Link href="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link>
            <a href="mailto:lookmaxers.ai@gmail.com" className="hover:text-white transition-colors">Contact</a>
          </div>
          <p className="text-sm text-white/30">© 2026 LookMax AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
